import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { Skeleton } from '@/components/ui/skeleton'
import { useServiceWithAddonConfig } from '@/modules/Service/services'
import { ServiceInformationProps, ServiceStatus } from '@/modules/Service/types'
import React, { useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertError } from 'vnetwork-ui'
import {
    createVCloudColumns,
    transformVCloudData,
    filterVCloudRows,
    detectColumnVisibility,
    getVCloudRowClassName,
    useVCloudActions,
    VCloudRow,
} from './vcloud-config'
import SelectProvider from './SelectProvider'

const Information: React.FC<ServiceInformationProps> = ({ service }) => {
    const { data, isLoading, isError, error, isPending } =
        useServiceWithAddonConfig(service?.service_id)
    const { t } = useTranslation(['services', 'customer'])

    // Expanded state for addon services (default: all expanded)
    const [expandedServices, setExpandedServices] = useState<Set<string>>(
        new Set()
    )

    // Transform data using VCloud-specific transformer
    const allRows = useMemo(
        () => transformVCloudData({ service, data, t }),
        [data, service, t]
    )

    // No initialization needed - default is collapsed (empty set)

    // Toggle expand/collapse for a service
    const handleToggleExpand = useCallback((serviceId: string) => {
        setExpandedServices((prev) => {
            const next = new Set(prev)
            if (next.has(serviceId)) {
                next.delete(serviceId)
            } else {
                next.add(serviceId)
            }
            return next
        })
    }, [])

    // Filter rows based on expanded state
    const rows = useMemo(
        () => filterVCloudRows(allRows, expandedServices),
        [allRows, expandedServices]
    )

    // Detect which columns should be visible based on data
    const columnVisibility = useMemo(
        () => detectColumnVisibility(allRows),
        [allRows]
    )

    const columns = useMemo(
        () =>
            createVCloudColumns(
                t,
                service?.service_pricing_model,
                expandedServices,
                handleToggleExpand,
                columnVisibility
            ),
        [
            t,
            service?.service_pricing_model,
            expandedServices,
            handleToggleExpand,
            columnVisibility,
        ]
    )
    const actions = useVCloudActions(t)

    // Hidden columns based on service status
    const hiddenColumns = useMemo(() => {
        const hidden = new Set<string>()
        if (service?.service_status !== ServiceStatus.Cancelled) {
            hidden.add('service_stopped_date')
        }
        const hideRenewStatuses = [
            ServiceStatus.PendingTerminated,
            ServiceStatus.Cancelled,
        ]
        if (
            hideRenewStatuses.includes(
                service?.service_status as ServiceStatus
            ) ||
            !service?.continue_startdate
        ) {
            hidden.add('service_renew_cycling')
        }
        return hidden
    }, [service?.service_status, service?.continue_startdate])

    if (isError) return <AlertError error={error} />
    if (isLoading || isPending) {
        return (
            <div className="space-y-2 p-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
            </div>
        )
    }

    // Show PAYG message if no commitment data
    if (allRows.length === 0) {
        return (
            <div className="flex items-center justify-center p-8 bg-blue-50 rounded-md border border-blue-200 mt-2">
                <div className="text-center">
                    <p className="text-2xl font-semibold text-blue-700 mb-2">
                        {t('services.info.payg', { defaultValue: 'PAYG' })}
                    </p>
                    <p className="text-sm text-gray-600">
                        {t('services.info.payg-description', {
                            defaultValue:
                                'Pay As You Go - No commitment resources',
                        })}
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-2">
            <DataTableCustomized<VCloudRow>
                columns={columns}
                rows={rows}
                rowKey={(row) => row._rowId}
                rowClassName={getVCloudRowClassName}
                size="small"
                toolbar={{ hiddenColumns }}
                actions={actions}
            />
            <SelectProvider
                serviceID={service?.service_id}
                serviceUUID={service?.service_creatable_uuid}
                serviceTemplate={service?.service_template}
            />
        </div>
    )
}

export default Information
